import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import {
  IconButton,
  showTagDialog,
  TextField,
} from '../../../../../components';
import { useAccount, useStore } from '../../../../../store';
import type { EventOrValue } from '../../../../../store/utils/extractValue';
import type Product from '../../../store/Product';
import type Version from '../store/Version';
import type VersionFile from '../store/VersionFile';
import type Versions from '../store/Versions';
import { showFileBrowserDialog } from './browser';
import classes from './FilesView.module.scss';

export default observer(function FilesView({
  product,
  version,
}: {
  product: Product;
  version: Version;
}) {
  const { productStore } = useStore();

  const { canBeChanged } = version;
  const account = useAccount();
  const { cdn } = account.configurations;

  async function showFileBrowser(file: VersionFile) {
    if (cdn.enabled && product.useCdn) {
      version.addCdnSettings(cdn);
    }

    const files = await showFileBrowserDialog({
      pulseCdnOptions: {
        productInfo: {
          id: product.id,
          isNew: product.isNew,
          name: product.name,
          sku: product.sku,
        },
      },
      startUrl: file.url,
    });
    version.replaceFileByS3Files(file, files);
  }

  function createOnChange(file: VersionFile) {
    return (event: EventOrValue) => {
      if (cdn.enabled && product.useCdn) {
        file.addCdnSettings(cdn);
      }

      file.handleChange(event);
      productStore.checkFileExistence(file);
    };
  }

  return (
    <>
      {version.files.filter(Boolean).map((file, index) => (
        <Box alignItems="baseline" display="flex" key={file.id}>
          <TextField
            disabled={!canBeChanged}
            errorText={!file.isChecking && file.errors.url}
            helperText={
              canBeChanged &&
              file.isChecking && (
                <span className={classes.warning}>File is checking…</span>
              )
            }
            label={file.getLabel(index)}
            name="url"
            onBlur={file.handleBlur}
            onChange={createOnChange(file)}
            value={file.url}
          />
          {canBeChanged && (
            <>
              <IconButton
                icon="folder"
                onClick={() => showFileBrowser(file)}
                title="Browse files"
              />

              {file.url && (
                <>
                  <VariationSelect file={file} versions={product.versions} />

                  <IconButton
                    icon="times"
                    onClick={file.delete}
                    title="Delete file"
                  />
                </>
              )}
            </>
          )}
        </Box>
      ))}
    </>
  );
});

const VariationSelect = observer(function VariationSelect({
  file,
  versions,
}: {
  file: VersionFile;
  versions: Versions;
}) {
  async function onClick() {
    const tag = await showTagDialog({
      createNewTitle: 'Create New Variation',
      options: versions.getVariations().map((variation) => ({
        label: variation,
        value: variation,
      })),
      selected: file.variation,
      title: 'Set File Variation',
      onDelete(variation) {
        versions.updateVariation(variation, '');
      },
      onRename(oldVariation, newVariation) {
        versions.updateVariation(oldVariation, newVariation);
      },
    });

    if (tag !== 'cancel') {
      file.handleChange({ name: 'variation', value: tag });
    }
  }

  return <IconButton icon="tag" onClick={onClick} title="Set file variation" />;
});
